import {
    ExamAppealDTO,
    SubmitAppealRequest,
    ReviewAppealRequest,
    AppealEligibilityResponse
} from "@/types/examination.types";
import axios from "@/axios.config";
import { AxiosError } from "axios";
import { ApiResponse } from "@/types/fetch.type";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Submit exam appeal (supports both theory and practical appeals)
export const useSubmitAppeal = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<ExamAppealDTO>, AxiosError<{ message: string }>, SubmitAppealRequest>({
        mutationFn: async (data) => {
            // Validate XOR: exactly one of examResultId or practicalExamResultId must be provided
            if (!data.examResultId && !data.practicalExamResultId) {
                throw new Error('Either examResultId or practicalExamResultId must be provided');
            }
            if (data.examResultId && data.practicalExamResultId) {
                throw new Error('Cannot provide both examResultId and practicalExamResultId');
            }

            const formData = new FormData();
            if (data.examResultId) {
                formData.append('examResultId', data.examResultId);
            }
            if (data.practicalExamResultId) {
                formData.append('practicalExamResultId', data.practicalExamResultId);
            }
            formData.append('appealReason', data.appealReason);
            if (data.supportingDocument) {
                formData.append('supportingDocument', data.supportingDocument);
            }

            const response = await axios.post<ApiResponse<ExamAppealDTO>>(`/exam-appeals/submit`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Appeal submitted successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ['exam-appeals'] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to submit appeal',
                color: 'red'
            });
        }
    });
};

// Get appeals by user ID
export const useGetUserAppeals = (userId?: string) => {
    return useQuery<ApiResponse<ExamAppealDTO[]>, AxiosError>({
        queryKey: ["exam-appeals", "user", userId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamAppealDTO[]>>(`/exam-appeals/user/${userId}`);
            return response.data;
        },
        enabled: !!userId,
    });
};

// Check appeal eligibility (supports both theory and practical exam results)
export const useCheckAppealEligibility = (examResultId?: string, practicalExamResultId?: string) => {
    return useQuery<ApiResponse<AppealEligibilityResponse>, AxiosError>({
        queryKey: ["exam-appeals", "eligibility", examResultId, practicalExamResultId],
        queryFn: async () => {
            let url = '/exam-appeals/eligibility?';
            if (examResultId) {
                url += `examResultId=${examResultId}`;
            } else if (practicalExamResultId) {
                url += `practicalExamResultId=${practicalExamResultId}`;
            }
            const response = await axios.get<ApiResponse<AppealEligibilityResponse>>(url);
            return response.data;
        },
        enabled: !!(examResultId || practicalExamResultId),
    });
};

// Get pending appeals (optionally filtered by councilId)
export const useGetPendingAppeals = (councilId?: string) => {
    return useQuery<ApiResponse<ExamAppealDTO[]>, AxiosError>({
        queryKey: ["exam-appeals", "pending", councilId],
        queryFn: async () => {
            const url = councilId 
                ? `/exam-appeals/pending?councilId=${councilId}`
                : `/exam-appeals/pending`;
            const response = await axios.get<ApiResponse<ExamAppealDTO[]>>(url);
            return response.data;
        },
    });
};

// Get appeals by council ID - Updated to support education program filtering
export const useGetAppealsByCouncil = (councilId?: string, educationProgramId?: string) => {
    return useQuery<ApiResponse<ExamAppealDTO[]>, AxiosError>({
        queryKey: ["exam-appeals", "council", councilId, educationProgramId],
        queryFn: async () => {
            const url = educationProgramId 
                ? `/exam-appeals/council/${councilId}?educationProgramId=${educationProgramId}`
                : `/exam-appeals/council/${councilId}`;
            const response = await axios.get<ApiResponse<ExamAppealDTO[]>>(url);
            return response.data;
        },
        enabled: !!councilId,
    });
};

// 🆕 Get appeals by education program ID
export const useGetAppealsByEducationProgram = (educationProgramId?: string) => {
    return useQuery<ApiResponse<ExamAppealDTO[]>, AxiosError>({
        queryKey: ["exam-appeals", "education-program", educationProgramId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamAppealDTO[]>>(`/exam-appeals/education-program/${educationProgramId}`);
            return response.data;
        },
        enabled: !!educationProgramId,
    });
};

// Review an appeal
export const useReviewAppeal = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<ExamAppealDTO>, AxiosError<{ message: string }>, { appealId: string; data: ReviewAppealRequest }>({
        mutationFn: async ({ appealId, data }) => {
            const response = await axios.put<ApiResponse<ExamAppealDTO>>(`/exam-appeals/${appealId}/review`, data);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Appeal reviewed successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ['exam-appeals'] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to review appeal',
                color: 'red'
            });
        }
    });
};

// Get appeal details
export const useGetAppealDetails = (appealId?: string) => {
    return useQuery<ApiResponse<ExamAppealDTO>, AxiosError>({
        queryKey: ["exam-appeals", appealId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ExamAppealDTO>>(`/exam-appeals/${appealId}`);
            return response.data;
        },
        enabled: !!appealId,
    });
}; 